import styled from 'styled-components'
import { media } from '../../config/settings/media'

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1.15rem;
  margin: 1rem 0 1.5rem;

  ${media.md`
    gap: 1.35rem;
    margin: 0.75rem 0 1.75rem;
  `}
`

export const ErrorText = styled.p`
  margin: 0;
  padding: 0.9rem 1.1rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.action.danger.bg}33;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(220, 38, 38, 0.12)'
      : 'rgba(220, 38, 38, 0.06)'};
  color: ${({ theme }) => theme.colors.action.danger.bg};
  font-size: 0.875rem;
  font-weight: 500;
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 2.25rem 1.25rem;
  border-radius: 12px;
  border: 1px dashed ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};
  text-align: center;

  strong {
    font-size: 0.9375rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  span {
    font-size: 0.8125rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`

export const PensionCard = styled.article`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid
    ${({ $emphasis, $overdue, theme }) => {
      if ($overdue) return `${theme.colors.action.danger.bg}55`
      if ($emphasis) return `${theme.colors.action.primary.bg}66`
      return theme.colors.borderDefault
    }};
  background: ${({ theme }) => theme.colors.background};
  box-shadow: ${({ $emphasis, $overdue, theme }) => {
    if ($overdue) {
      return theme.name === 'dark'
        ? 'inset 3px 0 0 0 rgba(220, 38, 38, 0.9)'
        : 'inset 3px 0 0 0 #DC2626'
    }
    if ($emphasis) {
      return theme.name === 'dark'
        ? 'inset 3px 0 0 0 rgba(62, 207, 142, 0.9)'
        : 'inset 3px 0 0 0 #3ECF8E'
    }
    return 'none'
  }};
`

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.9rem 1rem 0.8rem;
`

export const CardTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${media.sm`
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.25rem;
  `}
`

export const CardIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  flex: 1;
`

export const CardPeriod = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const StatusChip = styled.span`
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ $tone, theme }) => {
    if ($tone === 'overdue') return theme.colors.action.danger.bg
    if ($tone === 'due') return theme.colors.action.primary.bgActive
    return theme.colors.textMuted
  }};
`

export const CardUser = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: break-word;
`

export const CardValueWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;

  ${media.sm`
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-start;
    gap: 0.25rem;
  `}
`

export const CardValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;

  ${media.sm`
    font-size: 1.3rem;
  `}
`

export const CardFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.35rem 0.75rem;
  padding: 0.65rem 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ $paid, $upcoming, theme }) => {
    if ($paid) {
      return theme.name === 'dark'
        ? 'rgba(22, 101, 52, 0.16)'
        : 'rgba(22, 101, 52, 0.06)'
    }
    if ($upcoming) {
      return theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.02)'
        : theme.colors.borderSubtle
    }
    return theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.02)'
      : theme.colors.borderSubtle
  }};
`

export const PaymentCopy = styled.p`
  margin: 0;
  min-width: 0;
  flex: 1;
  font-size: 0.8125rem;
  font-weight: ${({ $paid }) => ($paid ? 600 : 500)};
  line-height: 1.4;
  color: ${({ $paid, theme }) =>
    $paid
      ? theme.name === 'dark'
        ? '#86efac'
        : '#166534'
      : theme.colors.textSecondary};
`

export const PaymentLink = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: 1.75rem;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.action.primary.bg};
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.75;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

export const UndoLink = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.35;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  text-decoration-color: ${({ theme }) => theme.colors.borderStrong};
  transition: color 0.15s ease;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.borderStrong};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

export const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.1rem;
`

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    color: ${({ theme, $danger }) =>
      $danger ? theme.colors.action.danger.bg : theme.colors.action.primary.bg};
    background: ${({ theme, $danger }) =>
      $danger
        ? `${theme.colors.action.danger.bg}29`
        : `${theme.colors.action.primary.bg}29`};
    border-color: ${({ theme, $danger }) =>
      $danger
        ? `${theme.colors.action.danger.bg}33`
        : `${theme.colors.action.primary.bg}33`};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme, $danger }) =>
        $danger
          ? theme.colors.action.danger.bg
          : theme.colors.action.primary.bg};
    outline-offset: 2px;
  }
`

export const UserGroup = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const UserGroupHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
`

export const UserGroupTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 650;
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const SectionHint = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const YearGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const YearLabel = styled.h3`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const CardsWrapper = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1fr;
`
